import { useCallback, useEffect, useRef, useState } from 'react';

import { getHeaders } from '@core/actions/xhrRequest';
import { subscribeToOrdersChanged } from '../helpers/ordersChangedSignal';

// Fetches the customer's open bidding orders for the "Finish your order"
// banner. Refetches automatically on:
//   · mount / `enabled` flip to true
//   · tab focus (`visibilitychange` + `focus`) — returns-after-a-while
//     keeps the banner in sync without polling
//   · cross-tab signal via `subscribeToOrdersChanged` — when another
//     tab hires / cancels / creates an order, this tab refetches so
//     the banner reflects server truth without a manual reload
//
// No polling — the above triggers cover every realistic state change.

// "Open" = still awaiting payment. The list endpoint doesn't ship
// is_paid/hired_bid_id flags (so the old check was always-true → the banner
// showed on paid orders), but it DOES ship the authoritative `statusDisplay`
// (OrderStatusForCustomerManager). An order is nudge-worthy only while its
// status is `unpaid`; paid / in-progress / completed / cancelled all carry a
// different code and are excluded.
function pickOpen(list) {
    return list.find(o => (
        o
        && (o.unique_number || o.uniqueNumber)
        && o.statusDisplay?.code === 'unpaid'
    )) || null;
}

export default function useActiveBiddingOrder({ enabled = true } = {}) {
    const [order, setOrder] = useState(null);
    const [bidsCount, setBidsCount] = useState(0);
    const [loading, setLoading] = useState(enabled);

    // Ref flag shared across all refetch triggers so we can cancel the
    // latest in-flight response if the component unmounts or `enabled`
    // flips off mid-fetch.
    const cancelledRef = useRef(false);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    const fetchNow = useCallback(() => {
        if (!enabledRef.current) return;
        cancelledRef.current = false;
        setLoading(true);
        fetch('/api/bidding/orders', {
            method: 'GET',
            credentials: 'include',
            headers: getHeaders(),
        })
            .then(r => (r.ok ? r.json() : Promise.reject(r)))
            .then(data => {
                if (cancelledRef.current) return;
                const list = Array.isArray(data)
                    ? data
                    : (Array.isArray(data?.orders) ? data.orders : []);
                const open = pickOpen(list);
                setOrder(open);
                // List ships the count under `bids.count`.
                setBidsCount(open?.bids?.count ?? open?.bids_count ?? 0);
            })
            .catch(() => { /* swallow — banner just doesn't show */ })
            .finally(() => {
                if (!cancelledRef.current) setLoading(false);
            });
    }, []);

    // Initial fetch whenever the hook is enabled.
    useEffect(() => {
        if (!enabled) return undefined;
        fetchNow();
        return () => { cancelledRef.current = true; };
    }, [enabled, fetchNow]);

    // Refresh whenever the customer focuses this tab — covers the
    // "been in another tab for 30 minutes, something changed" case
    // without polling.
    useEffect(() => {
        if (!enabled) return undefined;

        const maybeFetch = () => {
            if (document.visibilityState === 'visible') fetchNow();
        };
        window.addEventListener('focus', maybeFetch);
        document.addEventListener('visibilitychange', maybeFetch);
        return () => {
            window.removeEventListener('focus', maybeFetch);
            document.removeEventListener('visibilitychange', maybeFetch);
        };
    }, [enabled, fetchNow]);

    // Cross-tab: another tab just mutated the orders list — refetch so
    // this tab's banner stays honest.
    useEffect(() => {
        if (!enabled) return undefined;
        return subscribeToOrdersChanged(fetchNow);
    }, [enabled, fetchNow]);

    return { order, bidsCount, loading, refetch: fetchNow };
}
